import React from 'react'
import PropTypes from 'prop-types'
import {AspectRatio, Box, Flex, Img, useBreakpointValue} from '@chakra-ui/react'
import {HeroBannerText} from '../hero-banner/hero-banner-text'
import {remCalc} from '../../../../../plugins/px-to-rem-babel-custom-plugin/utils/remCalc'
import {calculateVerticalPadding, getMediaHeight, getHerobannerGradient} from '../utils'
import useDamImage from '../../../custom-hooks/use-dam-image'

export const HeroBannerPro = ({
    isMaskHeaderActive = true,
    textBlock,
    variant = 'white', // theme can either be black or white
    height = 'big',
    maxWidth = remCalc(1920),
    hasPaddings = true,
    buttonText,
    countdown,
    ...props
}) => {
    const imageSetKey = `image_${height}`
    const imageSetObj = props?.[imageSetKey]

    const damImage = useDamImage(imageSetObj)

    const direction =
        textBlock?.tabletMobileAlignment === 'bottom-center' ? 'column' : 'column-reverse'
    const verticalPadding = calculateVerticalPadding(direction, hasPaddings)

    const isVideo = props?.videos?.desktop && props?.videos?.mobile

    const gradient = getHerobannerGradient(
        textBlock.desktopAlignment,
        props?.maskgroup?.opacity,
        isMaskHeaderActive,
    )

    const mediaHeight = getMediaHeight(height)
    const isHeightAuto = mediaHeight === 'auto'

    const videoSource = useBreakpointValue({
        base: props?.videos?.mobile,
        sm: props?.videos?.desktop,
    })

    return (
        <Box
            data-testid={`hero-banner-container-${direction}`}
            pb={verticalPadding}
            maxW={maxWidth}
            backgroundColor={variant === 'black' ? 'background.dark' : 'background.default'}
            w="100%"
            m="0 auto"
        >
            <Box
                data-testid={
                    isVideo
                        ? 'hero-banner-video'
                        : damImage?.url
                        ? 'hero-banner-image'
                        : 'hero-banner-text'
                }
                pt={isHeightAuto ? {base: '86%', md: '35%', lg: '10%'} : undefined}
                pb={isHeightAuto ? {lg: '10%'} : undefined}
                w="100%"
                aspectRatio={damImage?.ratio}
                maxW={maxWidth}
                position="relative"
                flexDir={direction}
                display={{
                    base: 'block',
                    lg: 'flex',
                }}
            >
                {isVideo ? (
                    <Box position={{base: 'inherit', lg: 'absolute'}} width="100%" height="100%">
                        <video
                            key={videoSource}
                            autoPlay
                            loop
                            muted
                            aria-hidden="true"
                            style={{objectFit: 'cover', height: '100%', width: '100%'}}
                        >
                            <source src={videoSource} type="video/mp4" />
                            Your browser does not support the video tag.
                        </video>
                    </Box>
                ) : (
                    (damImage.url || imageSetObj?.alt) && (
                        <AspectRatio
                            ratio={damImage.ratio}
                            position={{base: 'inherit', lg: 'absolute'}}
                            width={'100%'}
                            minHeight={{base: 'auto', lg: '100%'}}
                            pb={{base: remCalc(24), lg: 0}}
                        >
                            <Img src={damImage.url} alt={imageSetObj?.alt || ''} />
                        </AspectRatio>
                    )
                )}
                <Flex
                    width={'100%'}
                    minHeight={{base: 'auto', lg: '100%'}}
                    bgImage={{
                        base: 'none',
                        lg: gradient || 'none',
                    }}
                    pt={{base: 0, lg: remCalc(24)}}
                    zIndex={1}
                >
                    <HeroBannerText
                        variant={variant}
                        maxWidth={maxWidth}
                        countdown={countdown}
                        textBlock={textBlock}
                        hasPaddings={hasPaddings}
                        buttonText={buttonText}
                    />
                </Flex>
            </Box>
        </Box>
    )
}

HeroBannerPro.propTypes = {
    isMaskHeaderActive: PropTypes.bool,
    textBlock: PropTypes.shape({
        label: PropTypes.string,
        title: PropTypes.string,
        bodyCopy: PropTypes.string,
        desktopAlignment: PropTypes.string,
        tabletMobileAlignment: PropTypes.string,
    }),
    buttonText: PropTypes.object,
    variant: PropTypes.string,
    height: PropTypes.string,
    link: PropTypes.string,
    maxWidth: PropTypes.string,
    hasPaddings: PropTypes.bool,
    countdown: PropTypes.array,
    maskgroup: PropTypes.shape({
        opacity: PropTypes.string,
    }),
    videos: PropTypes.shape({
        desktop: PropTypes.string,
        mobile: PropTypes.string,
    }),
}
